/**
 * SETTINGS_MENU — the single canonical settings-menu list.
 *
 * Three surfaces render the same six-entry menu: the `/settings` landing
 * (SettingsMenuPage), the ☰ drawer (AccountSheet), and the desktop sidebar
 * (SettingsLayover's SettingsSidebar). They used to each type the list out by
 * hand, so one edit (a label, route, or subtitle) had to be applied three
 * times — and a missed copy left stale text on a surface (the dead "Units"
 * subtitle survived in two copies, T36). This is the data-only shared source;
 * each surface maps it.
 *
 * Data only: `data-testid` values stay per-consumer because two diverge between
 * the landing and the drawer (`settings-menu-account` vs `account-members`,
 * `settings-menu-app` vs `account-settings`) and are test-asserted, so the
 * shared list holds the stable `key` and lets each surface derive its own ids.
 * `desc` is the subtitle the landing + drawer show; the sidebar ignores it.
 *
 * Order is household-central first → app-utility last (MN1: one flat ordered
 * list, no Household/App group headings).
 */

import { routes } from '../../routes/paths';
import type { IconName } from '../../components';

export interface SettingsMenuEntry {
  /** Stable identity — drives each surface's `data-testid` derivation. */
  key: 'account' | 'preferences' | 'brands' | 'stores' | 'products' | 'billing' | 'app';
  icon: IconName;
  label: string;
  /** Subtitle shown on the landing + drawer; the sidebar omits it. */
  desc: string;
  to: string;
}

export const SETTINGS_MENU: SettingsMenuEntry[] = [
  {
    key: 'account',
    icon: 'users',
    label: 'Account & members',
    desc: 'Profiles, ages & exclusions',
    to: routes.settingsAccount,
  },
  {
    key: 'preferences',
    icon: 'home',
    label: 'Household preferences',
    desc: 'Composition, diet, eating style, habits',
    to: routes.settingsPreferences,
  },
  {
    key: 'brands',
    icon: 'bookmark',
    label: 'Brand & product preferences',
    desc: 'Brands & products you like & avoid, household-wide',
    to: routes.settingsBrands,
  },
  {
    key: 'stores',
    icon: 'pin',
    label: 'Store preferences',
    desc: 'What you buy at each store',
    to: routes.settingsStores,
  },
  {
    key: 'products',
    icon: 'box',
    label: 'Your products',
    desc: 'Your catalog & per-product settings',
    to: routes.products,
  },
  {
    key: 'billing',
    icon: 'card',
    label: 'Billing & subscriptions',
    desc: 'Your development support and backed ideas — change or cancel',
    to: routes.settingsBilling,
  },
  {
    key: 'app',
    icon: 'gear',
    label: 'App settings',
    desc: 'Notifications, location, sync',
    to: routes.settingsApp,
  },
];
